/**
 * Created by Bart Decorte
 * Date: 10/09/2021
 * Time: 17:27
 */
import ChunkedUpload from './ChunkedUpload';

const EventEmitter = require('events');

export default class MultiChunkedUpload extends EventEmitter {
  constructor() {
    // TODO maybe execute uploads through a web worker to avoid them being blocked by other JS code.

    super();
    this.files = [];
    this.active = false;
    this.maxConnections = 10;
    this.maxActiveUploads = Infinity;
    this.uploadsActive = 0;
    this.uploadsFailed = 0;
    this.successfulUploadIdentifiers = [];
    this.canceledUploadIdentifiers = [];
    this.failedUploadIdentifiers = [];
    this.successfulUploads = [];
    this.canceledUploads = [];
    this.failedUploads = [];
    this.nextIndex = 0;

    this.initQueue();
  }

  initQueue() {
    this.queue = [];
    this.workingQueue = [];
    return this;
  }

  push(files) {
    this.files = [...this.files, ...files];
    const pushOnQueue = files.map(file => new ChunkedUpload(file, { index: this.nextIndex++, maxConnections: 0 }));
    this.queue = [...this.queue, ...pushOnQueue];
    this.workingQueue = [...pushOnQueue.reverse(), ...this.workingQueue];
    return this;
  }

  chunkCount() {
    return this.queue.reduce((carry, chunkedUpload) => carry + chunkedUpload.chunkCount(), 0);
  }

  assignedConnections() {
    return this.activeUploads().reduce((carry, chunkedUpload) => carry + chunkedUpload.maxConnections, 0);
  }

  unassignedConnections() {
    return this.maxConnections - this.assignedConnections();
  }

  connectionsAvailable() {
    return this.unassignedConnections() > 0;
  }

  failedUploadTolerationCount() {
    return 0;
  }

  tooManyFailedUploads() {
    return this.uploadsFailed > this.failedUploadTolerationCount();
  }

  activeUploads() {
    return this.queue.filter(chunkedUpload => chunkedUpload.active);
  }

  execute() {
    if (!this.active) {
      this.active = true;
    }
    this.nextUpload();
  }

  cancelUploadAtIndex(index) {
    const upload = this.queue[index];
    this.canceledUploads.push(upload);
    upload.cancel();
  }

  maxSimultaneousUploadsReached() {
    if (this.maxActiveUploads === 0) {
      return false;
    }

    return this.uploadsActive >= this.maxActiveUploads;
  }

  nextUpload() {
    // Assign inactive connections to uploads already in progress
    let i = 0;
    const activeUploads = this.activeUploads();
    while (this.connectionsAvailable() && i < activeUploads.length) {
      const upload = activeUploads[i];
      upload.setMaxConnections(Math.min(upload.pendingChunkCount(), this.unassignedConnections()));
      i++;
    }

    // No more queued chunks to upload
    if (!this.workingQueue.length) {
      // No more active connections, looks like we're done
      if (!this.uploadsActive) {
        this.active = false;
        this.emitSuccess();
      }
      return;
    }

    // User might've canceled while upload was in progress
    if (this.canceled) {
      this.active = false;
      this.emitCancellation();
      return;
    }

    // We don't want to retry endlessly if too many requests are failing
    if (this.tooManyFailedUploads()) {
      this.active = false;
      this.emitFailure();
      return;
    }

    if (this.maxSimultaneousUploadsReached()) {
      return;
    }

    // Nothing to do right now, we're out of connections
    if (! this.connectionsAvailable()) {
      return;
    }

    const upload = this.workingQueue.pop();

    upload.on('canceled', () => {
      this.uploadsActive -= 1;
      this.canceledUploads.push(upload);
      this.nextUpload();
    });

    upload.on('failure', () => {
      this.uploadsActive -= 1;
      this.failedUploads.push(upload);
      this.nextUpload();
    });

    upload.on('progress', decimal => {
      this.emitProgress(upload);
      this.nextUpload();
    });

    upload.on('success', () => {
      this.uploadsActive -= 1;
      this.successfulUploads.push(upload);
      this.nextUpload();
    });

    upload.setMaxConnections(Math.min(upload.chunkCount(), this.unassignedConnections()));
    upload.execute();
    this.uploadsActive += 1;
    this.nextUpload();
  }

  pendingChunkCount() {
    return this.queue.reduce((carry, chunkedUpload) => carry + chunkedUpload.pendingChunkCount(), 0);
  }

  activeChunkCount() {
    return this.queue.reduce((carry, chunkedUpload) => carry + chunkedUpload.activeChunkCount(), 0);
  }

  progress() {
    return 1 - (this.pendingChunkCount() + this.activeChunkCount()) / this.chunkCount();
  }

  resultingUploads() {
    return this.successfulUploads.filter(upload => !this.canceledUploads.includes(upload));
  }

  result() {
    return this.resultingUploads().map(upload => upload.meta());
  }

  emitProgress(progressedUpload) {
    if (this.canceled) {
      return;
    }

    // progress: total chunks minus "to do" minus "doing"
    const progress = this.progress();

    this.emit('progress', {
      progress,
      upload: {
        index: this.queue.indexOf(progressedUpload),
        progress: progressedUpload.progress(),
      },
    });
  }

  emitSuccess() {
    if (this.canceled) {
      return;
    }

    this.emit('success', {
      result: this.result(),
    });
  }

  emitCancellation() {
    // Only emit once, other classes might be keeping count of busy instances
    if (this.canceledPropagated) {
      return;
    }
    this.emit('canceled');
    this.canceledPropagated = true;
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/components/uploads/MultiChunkedUpload.js